import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Descriptions,
  Icon,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import { api } from "../api/client.js";
import {
  addOrgMember,
  changeOrgMemberRole,
  getOrgDetail,
  removeOrgMember,
  setOrgEnterpriseSize,
} from "../api/dpp.js";
import { ENTERPRISE_SIZES } from "../types.js";
import type { EnterpriseSize } from "../types.js";
import { ErrorAlert } from "../errors/index.js";
import { useAuth } from "../auth/AuthContext.js";
import { useCan } from "../auth/useCan.js";
import { ApiKeysCard } from "../dpp/ApiKeysCard.js";
import { InvitesCard } from "../dpp/InvitesCard.js";
import { OperatorsCard } from "../dpp/OperatorsCard.js";
import { ThemeCard } from "../dpp/ThemeCard.js";
import { WebhooksCard } from "../dpp/WebhooksCard.js";
import { useT, useFormatDateTime } from "../i18n.js";
import { NoOrgState } from "../orgs/NoOrgState.js";
import { useOrgs } from "../orgs/OrgContext.js";
import type { OrgDetail, OrgMemberRole, PublicUser } from "../types.js";

export function OrgSettingsPage() {
  const t = useT();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const { user, hasPermission } = useAuth();
  const can = useCan();
  const { org, loading: orgsLoading } = useOrgs();

  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [error, setError] = useState<unknown>(null);

  // Add-member modal (user options come from the admin list — user:read only)
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<OrgMemberRole>("editor");
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState<unknown>(null);

  const canListUsers = hasPermission("user:read");

  const load = async (orgId: string) => {
    setError(null);
    try {
      setDetail(await getOrgDetail(orgId));
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    if (org) void load(org.id);
  }, [org?.id]);

  useEffect(() => {
    if (!canListUsers) return;
    api<PublicUser[]>("/admin/users?limit=200")
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [canListUsers]);

  // Membership management: the org's own admin, or a platform admin (org:manage) —
  // mirrors the API's requireOrgAdmin guard.
  const canManage = detail?.memberRole === "admin" || hasPermission("org:manage");
  // Localized org-role options, reused by the members table and the add-member modal.
  const roleOptions = (["admin", "editor", "viewer"] as const).map((r) => ({
    label: t(`role.${r}`),
    value: r,
  }));

  const memberIds = useMemo(() => new Set(detail?.members.map((m) => m.userId) ?? []), [detail]);
  const candidateUsers = users.filter((u) => !memberIds.has(u.id) && !u.disabled);

  if (orgsLoading) return <Spin />;
  if (!org) return <NoOrgState />;
  if (error && !detail) return <ErrorAlert error={error} onRetry={() => org && void load(org.id)} />;
  if (!detail) return <Spin />;

  const submitAdd = async () => {
    if (!userId) return;
    setAddError(null);
    setSaving(true);
    try {
      await addOrgMember(org.id, userId, role);
      toast.success(t("orgSettings.memberAdded"));
      setOpen(false);
      setUserId("");
      await load(org.id);
    } catch (e) {
      setAddError(e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (memberUserId: string) => {
    setError(null);
    try {
      await removeOrgMember(org.id, memberUserId);
      toast.success(t("orgSettings.memberRemoved"));
      await load(org.id);
    } catch (e) {
      // Surfaces the API's 409 guard (an org must keep at least one admin).
      setError(e);
    }
  };

  const saveEnterpriseSize = async (size: EnterpriseSize | null) => {
    setError(null);
    try {
      await setOrgEnterpriseSize(org.id, size);
      toast.success(t("org.enterpriseSizeSaved"));
      await load(org.id);
    } catch (e) {
      setError(e);
    }
  };

  const changeRole = async (memberUserId: string, newRole: OrgMemberRole) => {
    setError(null);
    try {
      await changeOrgMemberRole(org.id, memberUserId, newRole);
      toast.success(t("orgSettings.roleChanged"));
      await load(org.id);
    } catch (e) {
      // Surfaces the API's 409 guard (an org must keep at least one admin).
      setError(e);
    }
  };

  return (
    <Space direction="vertical" size="lg" style={{ width: "100%" }}>
      <Card title={t("orgSettings.title")}>
        {error != null && <ErrorAlert error={error} />}
        <Descriptions
          column={2}
          items={[
            { label: t("org.name"), children: detail.name },
            {
              label: t("org.slug"),
              children: (
                <Text code copyable>
                  {detail.slug}
                </Text>
              ),
            },
            {
              label: t("billing.plan"),
              children: <Tag color="primary">{t(`billingPlan.${detail.billingPlan}`)}</Tag>,
            },
            { label: t("org.gs1Prefix"), children: detail.gs1CompanyPrefix ?? "—" },
            {
              label: t("org.enterpriseSize"),
              children: canManage ? (
                <div style={{ width: 200 }}>
                  <Select
                    options={[
                      { label: "—", value: "" },
                      ...ENTERPRISE_SIZES.map((s) => ({ label: t(`enterpriseSize.${s}`), value: s })),
                    ]}
                    value={detail.enterpriseSize ?? ""}
                    onChange={(v) => void saveEnterpriseSize(v === "" ? null : (v as EnterpriseSize))}
                  />
                </div>
              ) : detail.enterpriseSize ? (
                t(`enterpriseSize.${detail.enterpriseSize}`)
              ) : (
                "—"
              ),
            },
            {
              label: t("org.status"),
              children: (
                <Tag color={detail.status === "active" ? "success" : "warning"}>
                  {t(`orgStatus.${detail.status}`)}
                </Tag>
              ),
            },
            { label: t("common.createdAt"), children: fmtDate(detail.createdAt) },
            {
              label: t("orgSettings.yourRole"),
              children: detail.memberRole ? t(`role.${detail.memberRole}`) : "—",
            },
          ]}
        />
      </Card>

      <Card
        title={t("orgSettings.members")}
        extra={
          canManage && canListUsers ? (
            <Button
              size="sm"
              icon={<Icon name="plus" size={14} />}
              onClick={() => {
                setAddError(null);
                setOpen(true);
              }}
            >
              {t("orgSettings.addMember")}
            </Button>
          ) : undefined
        }
      >
        <Table
          rowKey={(m) => m.userId}
          empty={t("orgSettings.noMembers")}
          columns={[
            { key: "email", title: t("orgSettings.email"), render: (m) => m.email },
            {
              key: "name",
              title: t("orgSettings.user"),
              render: (m) => m.displayName ?? "—",
            },
            {
              key: "role",
              title: t("orgSettings.role"),
              render: (m) =>
                canManage ? (
                  <div style={{ minWidth: 120 }}>
                    <Select
                      size="sm"
                      options={roleOptions}
                      value={m.role}
                      onChange={(v) => void changeRole(m.userId, v as OrgMemberRole)}
                    />
                  </div>
                ) : (
                  <Tag color={m.role === "admin" ? "primary" : "default"}>{t(`role.${m.role}`)}</Tag>
                ),
            },
            { key: "since", title: t("common.createdAt"), render: (m) => fmtDate(m.createdAt) },
            {
              key: "actions",
              title: "",
              render: (m) =>
                canManage ? (
                  <Popconfirm
                    title={t("orgSettings.removeConfirm")}
                    description={t("orgSettings.removeDescription")}
                    okText={t("common.remove")}
                    cancelText={t("common.cancel")}
                    okType="danger"
                    placement="left"
                    onConfirm={() => void remove(m.userId)}
                  >
                    <Button
                      size="sm"
                      variant="danger"
                      icon={<Icon name="trash" size={14} />}
                      aria-label={t("common.remove")}
                      disabled={m.userId === user?.id && detail.members.length === 1}
                    />
                  </Popconfirm>
                ) : null,
            },
          ]}
          data={detail.members}
        />
      </Card>

      {/* Economic operators the passports name — readable by every member, admin-editable. */}
      <OperatorsCard orgId={org.id} canWrite={canManage} />

      {/* Email invitations — team onboarding, org admins only. */}
      {canManage && <InvitesCard orgId={org.id} />}

      {/* Machine integration (ERP bridge) — credential issuance, admin only. */}
      {can("integration:manage") && (
        <>
          <ThemeCard orgId={org.id} canWrite={detail?.memberRole === "admin"} />
          <ApiKeysCard orgId={org.id} />
          <WebhooksCard orgId={org.id} />
        </>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t("orgSettings.addMember")}
        okText={t("common.create")}
        cancelText={t("common.cancel")}
        onOk={() => void submitAdd()}
        confirmLoading={saving}
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          {addError != null && <ErrorAlert error={addError} onRetry={() => void submitAdd()} />}
          <Select
            label={t("orgSettings.user")}
            showSearch
            options={candidateUsers.map((u) => ({
              label: u.displayName ? `${u.displayName} (${u.email})` : u.email,
              value: u.id,
              searchText: `${u.displayName ?? ""} ${u.email}`,
            }))}
            value={userId}
            onChange={setUserId}
          />
          <Select
            label={t("orgSettings.role")}
            options={roleOptions}
            value={role}
            onChange={(v) => setRole(v as OrgMemberRole)}
          />
        </Space>
      </Modal>
    </Space>
  );
}
